import React, { type ReactNode } from 'react'

import { useMutation } from '@tanstack/react-query'
import { useModal } from '@app/hooks/useModal'
import { handleMultiStatusError } from '@app/utils'

import { ErrorModalContent, ErrorModalFooter } from './index'

import type { AxiosError } from 'axios'
import type { MultiStatusResponseDto } from '@app/api/dtos'
import type { MultiStatusMutationOptions, UseMultiStatusMutationReturn } from './types'

export function useMultiStatusMutation<DataType> (
  options: MultiStatusMutationOptions<DataType>
): UseMultiStatusMutationReturn<DataType> {
  const { onError, mutationFn, onSuccess, footer } = options

  const [modal, modalContextHolder] = useModal()

  const showErrorModal = (error: AxiosError<MultiStatusResponseDto>, customFooter?: ReactNode): void => {
    const results = error.response?.data?.results
    let processedResults = results
    if (results && !Array.isArray(results) && typeof results === 'object') {
      processedResults = [results]
    }
    const modalError = modal.error({
      title: 'Ошибка:',
      content: (
        <ErrorModalContent
          message={ error.message }
          data={ Array.isArray(processedResults) ? processedResults : undefined }
        />
      ),
      footer: <ErrorModalFooter onClose={ () => modalError.destroy() }>{ customFooter ?? footer }</ErrorModalFooter>
    })
  }

  const handleError = (error: AxiosError<MultiStatusResponseDto>): void => {
    onError?.(error)

    handleMultiStatusError(error, () => {
      showErrorModal(error)
    })
  }

  const mutation = useMutation({
    mutationFn,
    onSuccess,
    onError: handleError
  })

  return {
    mutation,
    modalContextHolder,
    showErrorModal
  }
}
